import React from 'react';
import { ToastService } from '@iamsoftware/react-hooks';
import { useDataTable } from 'src/hooks/useAppDataTable';
import { useNavigate } from 'react-router-dom';
import { AgreementService } from '../../../../../service/AgreementService';
import { DocumentNameCell } from '../DocumentNameCell';

interface CompletedTabProps {
    loadCanvas: (file: any) => void;
    setDisplayUploadAttachmentDialog: (data: { agreementId: string, agreementName: string } | null) => void;
    handleCancel: (item: any) => void;
    isSigning: boolean;
    setReload?: (reloadFn: () => void) => void;
    headerActions?: React.ReactNode;
}

export const CompletedTab: React.FC<CompletedTabProps> = ({
    loadCanvas,
    setDisplayUploadAttachmentDialog,
    handleCancel,
    isSigning,
    setReload,
    headerActions
}) => {
    const navigate = useNavigate();
    const columns: any[] = [
        {
            field: 'agreementName',
            header: 'Tên tài liệu',
            width: 100,
            matchMode: 'contains',
            dataType: 'custom' as const,
            customCell(row: any) {
                return (
                    <DocumentNameCell
                        rowData={row}
                        onClick={() => navigate(`/quan-ly-tai-lieu/tai-lieu/${row.agreementId}?mode=view`)}
                    />
                );
            }
        },
        { field: 'agreementNo', header: 'Mã tài liệu', width: 140, matchMode: 'contains' },
        { field: 'agreementTypeName', header: 'Loại tài liệu', width: 160, matchMode: 'contains' },
        // {
        //     field: 'statusDescription',
        //     header: 'Trạng thái',
        //     width: 200,
        //     dataType: 'custom' as const,
        //     customCell(row: any) {
        //         const isUrgent = row.isUrgent === 'Y';
        //         const isImportant = row.isImportant === 'Y';
        //         return (
        //             <div className="flex align-items-center gap-2 flex-wrap">
        //                 <Tag value="Hoàn thành" severity="success" />
        //                 {isUrgent && (
        //                     <span className="flex align-items-center gap-1 text-orange-500 text-sm" title="Tài liệu cần xử lý gấp">
        //                         <i className="pi pi-bolt"></i>
        //                         <span>Khẩn cấp</span>
        //                     </span>
        //                 )}
        //                 {isImportant && (
        //                     <span className="flex align-items-center gap-1 text-indigo-500 text-sm" title="Tài liệu quan trọng">
        //                         <i className="pi pi-bookmark"></i>
        //                         <span>Quan trọng</span>
        //                     </span>
        //                 )}
        //             </div>
        //         );
        //     }
        // },
        // {
        //     field: 'mainContent',
        //     header: 'File ký',
        //     width: 220,
        //     dataType: 'custom' as const,
        //     customCell(row: any) {
        //         if (!row.mainContent) return <span className="text-gray-500">Chưa có file</span>;
        //         return (
        //             <div className="flex align-items-center gap-2">
        //                 <i className={`pi ${row.mainContent.contentType === 'application/pdf' ? 'pi-file-pdf' : 'pi-file-word'} text-xl`}></i>
        //                 <div>
        //                     <div className="font-medium">{row.mainContent.fileName}</div>
        //                     <small className="text-gray-500">{row.mainContent.contentType?.split('/')?.[1]?.toUpperCase()}</small>
        //                 </div>
        //             </div>
        //         );
        //     }
        // },
        {
            field: 'expiryDate',
            header: 'Thời gian',
            width: 180,
            dataType: 'custom',
            customCell(rowData: any): any {
                const formatLocal = (value?: any) => {
                    if (!value) return null;
                    const d = new Date(value);
                    const dd = String(d.getDate()).padStart(2, '0');
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    const HH = String(d.getHours()).padStart(2, '0');
                    const MM = String(d.getMinutes()).padStart(2, '0');
                    const SS = String(d.getSeconds()).padStart(2, '0');
                    return `${dd}/${mm}/${yyyy} ${HH}:${MM}:${SS}`;
                };

                const expiryText = rowData.expiryDate ? formatLocal(rowData.expiryDate) : null;
                const updatedText = rowData.lastUpdatedStamp ? formatLocal(rowData.lastUpdatedStamp) : null;

                if (!expiryText && !updatedText) return null;

                return (
                    <div className="text-sm">
                        {expiryText && <div className={updatedText ? 'mb-1' : undefined}>{expiryText}</div>}
                        {updatedText && (
                            <div>{updatedText}</div>
                        )}
                    </div>
                );
            }
        }
    ];

    const mapListResult = (result: any) => {
        if (result.listData && Array.isArray(result.listData)) {
            result.listData = result.listData.map((item: any) => ({
                ...item,
                mainContent: item.mainFileName || item.mainContentType ? {
                    fileName: item.mainFileName,
                    contentType: item.mainContentType,
                    contentLocation: item.referenceContentLocation || item.mainContentLocation,
                    fileId: item.fileId || item.mainFileId || item.mainContentId,
                    googleFileId: item.fileId || item.mainFileId || item.mainContentId,
                    referenceFileId: item.referenceContentLocation
                } : null
            }));
        }
        const total = result.totalRecords ?? (result.listData || []).length;
        return {
            listData: result.listData || [],
            totalRecords: total,
            totalCount: total
        };
    };

    const { render: renderCompleted, reloadLazyData: reloadCompleted } = useDataTable({
        recordLabel: 'tài liệu hoàn thành',
        headerActions,
        dataKey: 'agreementId',
        indexColumnWidth: 56,
        actionColumnWidth: 170,
        columns: columns,
        getList: lazyLoadEvent => {
            return AgreementService.getAgreementList(lazyLoadEvent, 'AGT_SIGN_COMPLETED').then(mapListResult);
        },
        initActions(items: Array<any>) {
            items?.forEach(item => {
                const mainFileId = item?.mainContent?.fileId || item?.fileId;
                const hasMainFileUrl = item?.mainContent?.contentLocation;
                const actions: any[] = [];

                if (mainFileId || hasMainFileUrl) {
                    actions.push({
                        icon: 'pi pi-external-link',
                        title: 'Xem file',
                        className: 'p-button-help',
                        command: () => {
                            if (item.mainContent) {
                                loadCanvas(item.mainContent);
                            } else {
                                ToastService.error('Không tìm thấy file để xem');
                            }
                        }
                    });
                }
                actions.push({
                    icon: 'pi pi-paperclip',
                    title: 'File đính kèm',
                    className: 'p-button-info',
                    props: {
                        badge: Number(item.attachmentCount) > 0 ? String(item.attachmentCount) : undefined,
                        badgeClassName: 'p-badge-info'
                    },
                    command: () => {
                        setDisplayUploadAttachmentDialog({ agreementId: item.agreementId, agreementName: item.agreementName });
                    }
                });
                actions.push({
                    icon: 'pi pi-times',
                    title: 'Hủy',
                    className: 'p-button-danger',
                    command: () => { if (!isSigning) handleCancel(item); }
                });

                item.actions = actions;
            });
        },
        originalProps: { scrollHeight: 'calc(100vh - 300px)' }
    });

    React.useEffect(() => {
        if (setReload) setReload(reloadCompleted);
    }, [reloadCompleted, setReload]);

    return (
        <div className="tai-lieu-ky-table">
            {renderCompleted()}
        </div>
    );
};
